/**
 * Slide Screen Component
 *
 * A chat-like slide wrapper component that provides consistent styling
 * and animation for each step of the wizard.
 *
 * Features:
 * - Slide-up animation when entering
 * - White card with rounded corners
 * - Navigation buttons at bottom
 * - Headline text at top
 *
 * @see {@link ./__tests__/slide-screen.test.tsx}
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '@bnsn/ui/components/button'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { cn } from '@bnsn/ui/lib/utils'
import { TutorialVideoButton } from './tutorial-video-button'

interface SlideScreenProps {
  /** Headline text displayed at the top */
  headline: string
  /** Main content of the slide */
  children: ReactNode
  /** Callback when back button is clicked */
  onBack?: () => void
  /** Callback when continue button is clicked */
  onContinue?: () => void
  /** Whether the continue button should be disabled */
  continueDisabled?: boolean
  /** Whether the form is currently submitting */
  isSubmitting?: boolean
  /** Custom continue button text */
  continueText?: string
  /** Custom text shown while submitting (defaults to "Processing...") */
  submittingText?: string
  /** Whether to show the back button */
  showBack?: boolean
  /** Whether to show the continue button */
  showContinue?: boolean
  /** Optional className for the container */
  className?: string
  /** Optional className for the card */
  cardClassName?: string
  /** Optional footer content (replaces default navigation) */
  footer?: ReactNode
  /** Optional tutorial video embed code. When provided, shows a play button in the header. */
  tutorialVideoEmbed?: string | null
  /** Whether to center the headline text */
  centerHeadline?: boolean
}

/**
 * Slide Screen Component
 *
 * Provides a consistent card-like container for each wizard step
 * with a slide-up animation effect.
 */
export const SlideScreen = ({
  headline,
  children,
  onBack,
  onContinue,
  continueDisabled = false,
  isSubmitting = false,
  continueText = 'Continue',
  submittingText = 'Processing...',
  showBack = true,
  showContinue = true,
  className,
  cardClassName,
  footer,
  tutorialVideoEmbed,
  centerHeadline = false,
}: SlideScreenProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false)

  // Trigger slide-up animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 50)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      className={cn(
        // Base styles
        'w-full max-w-4xl mx-auto',
        // Animation
        'transform transition-all duration-500 ease-out',
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
        className,
      )}
    >
      {/* Card container */}
      <div className={cn('bg-white rounded-xl shadow-lg p-6 sm:p-8', cardClassName)}>
        {/* Headline with optional tutorial button */}
        <div className={cn('flex items-start justify-between gap-2 mb-6', centerHeadline && 'justify-center')}>
          <h2 className={cn('text-xl sm:text-2xl font-semibold text-gray-900', centerHeadline && 'text-center')}>
            {headline}
          </h2>
          {tutorialVideoEmbed && <TutorialVideoButton embedCode={tutorialVideoEmbed} />}
        </div>

        {/* Main content */}
        <div className="space-y-6">{children}</div>

        {/* Navigation footer */}
        {footer === undefined ? (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            {/* Back button */}
            {showBack && onBack ? (
              <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {/* Continue button */}
            {showContinue && onContinue && (
              <Button type="button" onClick={onContinue} disabled={continueDisabled || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {submittingText}
                  </>
                ) : (
                  <>
                    {continueText}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            )}
          </div>
        ) : (
          footer
        )}
      </div>
    </div>
  )
}

export default SlideScreen
